// src/features/auth/pages/AuthPage.tsx

import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { Box, Container, Paper, Typography, Tabs, Tab } from "@mui/material";
import AppFooter from "../../../components/common/AppFooter/AppFooter";
import { Toast } from "../../../components/common/Toast/Toast";
import { LoginForm } from "../components/LoginForm";
import { SignUpForm } from "../components/SignUpForm";
import { RegistrationComplete } from "../components/RegistrationComplete";
import { authService } from "../services/authService";
import { LoginCredentials, SignUpData } from "../types";
import { addressService } from "../../myAccount/myAddress/services/addressService";

// TabPanel Component
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

// Main AuthPage Component
export const AuthPage: React.FC = () => {
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [showComplete, setShowComplete] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState("");
  const [isSignUpLogin, setIsSignUpLogin] = useState(false);
  const signUpDataRef = useRef<SignUpData | null>(null);
  const [toast, setToast] = useState<{ open: boolean; message: string; severity: "success" | "error" }>({
    open: false,
    message: "",
    severity: "error",
  });

  // ถ้า login แล้ว (มี token) redirect ไปหน้าหลัก (ยกเว้นกำลังแสดงหน้า complete)
  useEffect(() => {
    if (authService.isAuthenticated() && !showComplete) {
      navigate("/", { replace: true });
    }
  }, [navigate, showComplete]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setShowComplete(false);
  };

  // Login Mutation
  const loginMutation = useMutation({
    mutationFn: (credentials: LoginCredentials) =>
      authService.login(credentials),
    onSuccess: async (data) => {
      if (isSignUpLogin) {
        const signUpData = signUpDataRef.current;
        if (
          signUpData &&
          (signUpData.address || signUpData.country || signUpData.postcode)
        ) {
          try {
            await addressService.createAddress({
              title: "Home",
              firstName: signUpData.firstName,
              lastName: signUpData.lastName,
              phoneCode: signUpData.phoneCode,
              phoneNumber: signUpData.phoneNumber,
              country: signUpData.country,
              address: signUpData.address,
              province: signUpData.province,
              district: signUpData.district,
              postcode: signUpData.postcode,
              isDefault: true,
            });
          } catch {
            // address creation failure ไม่ block signup completion
          }
        }
        signUpDataRef.current = null;
        setRegisteredEmail(data.user?.email || "");
        setShowComplete(true);
        setIsSignUpLogin(false);
      } else {
        navigate("/");
      }
    },
    onError: (error: Error) => {
      console.error("Login failed:", error.message);
      setIsSignUpLogin(false);
    },
  });

  // Sign Up Mutation
  const signUpMutation = useMutation({
    mutationFn: (data: SignUpData) => authService.signUp(data),
    onSuccess: (_data, variables) => {
      signUpDataRef.current = variables;
      setIsSignUpLogin(true);
      loginMutation.mutate({
        email: variables.email,
        password: variables.password,
      });
    },
    onError: (error: Error) => {
      setToast({ open: true, message: error.message, severity: "error" });
    },
  });

  const handleLogin = (credentials: LoginCredentials) => {
    loginMutation.mutate(credentials);
  };

  const handleSignUp = (data: SignUpData) => {
    signUpMutation.mutate(data);
  };

  // Show Registration Complete Page
  if (showComplete) {
    return (
      <Box sx={{ minHeight: "100vh", bgcolor: "#ffffff" }}>
        <Container maxWidth="xl">
          <Box sx={{ pt: 8 }}>
            <Paper elevation={0} sx={{ p: 4, bgcolor: "white" }}>
              <RegistrationComplete email={registeredEmail} />
            </Paper>
          </Box>
        </Container>
        <AppFooter />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#ffffff", marginTop: "60px" }}>
      <Container maxWidth="md">
        <Box sx={{ pt: 8, pb: 4 }}>
          <Paper elevation={0} sx={{ p: 4, bgcolor: "white" }}>
            <Typography
              variant="h4"
              component="h1"
              align="center"
              sx={{ mb: 3, fontWeight: "normal", fontFamily: "Nandia" }}
            >
              Account
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
              align="center"
              sx={{ mb: 3 }}
            >
              Welcome to SIRIVANNAVARI. <br />
              Sign in to enjoy a personalised shopping experience and exclusive member privileges.
            </Typography>

            {/* Tabs */}
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              centered
              sx={{
                "& .MuiTabs-indicator": {
                  backgroundColor: "#000",
                  height: 2,
                },
                "& .MuiTab-root": {
                  fontSize: "1.1rem",
                  fontWeight: 400,
                  minWidth: 100,
                  textTransform: "none",
                  color: "text.disabled",
                  "&.Mui-selected": {
                    color: "#000",
                    fontWeight: 600,
                  },
                },
              }}
            >
              <Tab label="Login" />
              <Tab label="Sign up" />
            </Tabs>

            {/* Tab Panels */}
            <TabPanel value={tabValue} index={0}>
              <LoginForm
                onSubmit={handleLogin}
                isLoading={loginMutation.isPending}
                error={loginMutation.error?.message || null}
              />
            </TabPanel>

            <TabPanel value={tabValue} index={1}>
              <SignUpForm
                onSubmit={handleSignUp}
                isLoading={signUpMutation.isPending}
                error={signUpMutation.error?.message || null}
              />
            </TabPanel>
          </Paper>
        </Box>
      </Container>

      <AppFooter />
      <Toast
        open={toast.open}
        message={toast.message}
        severity={toast.severity}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
      />
    </Box>
  );
};
